import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import {
  FiHash, FiMessageSquare, FiTrendingUp, FiRefreshCw,
  FiStar, FiBarChart2, FiChevronDown, FiZap, FiSearch,
  FiPhone, FiFileText, FiLock, FiMapPin, FiPlus,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useChannels } from '../../context/ChannelContext';
import { useAmpsLoaded } from '../../context/AmpContext';
import type { Amp } from '../chat/chat.types';
import { ROUTES } from '../../app/routes';
import { listDwAgents } from '../claudeagents/claudeAgentApi';
import type { DwAgentRecord } from '../claudeagents/claudeAgentApi';
import '../../styles/ChannelAmpDropdown.css';

const specialAmpPath = (amp: Amp): string | null => {
  if (amp.name === 'MAC Workflows') return ROUTES.MAC_WORKFLOWS;
  if (amp.category === 'Momentum Voice' || amp.category === 'Voice') return ROUTES.VOICE;
  if (amp.category === 'Quote Proposal') return ROUTES.QUOTE_PROPOSAL;
  if (amp.category === 'Carrier Download') return ROUTES.CARRIER_DOWNLOAD;
  return null;
};

const CHANNEL_ICON_MAP: Record<string, React.ReactNode> = {
  hash: <FiHash size={13} />,
  message: <FiMessageSquare size={13} />,
  trending: <FiTrendingUp size={13} />,
  refresh: <FiRefreshCw size={13} />,
  star: <FiStar size={13} />,
  chart: <FiBarChart2 size={13} />,
};

const AMP_ICON_MAP: Record<string, React.ReactNode> = {
  message: <FiMessageSquare size={13} />,
  search: <FiSearch size={13} />,
  phone: <FiPhone size={13} />,
  file: <FiFileText size={13} />,
  image: <RiSparklingFill size={13} />,
  scan: <FiSearch size={13} />,
  dollar: <FiBarChart2 size={13} />,
  chart: <FiBarChart2 size={13} />,
  shield: <FiLock size={13} />,
  database: <FiHash size={13} />,
  car: <FiMapPin size={13} />,
  compare: <FiBarChart2 size={13} />,
  zap: <FiZap size={13} />,
};

interface ChannelAmpDropdownProps {
  onAddAmp?: () => void;
  selectedAmpId?: string | null;
  onAmpSelect?: (ampId: string) => void;
  /** Overrides the trigger label/icon (e.g. show a Digital Workforce agent's name instead of the AMP's). */
  overrideName?: string;
  /** The active Digital Workforce agent's id — when set alongside overrideName, the
   *  trigger becomes a live agent-switcher menu instead of the disabled channel/AMP one. */
  currentAgentId?: string;
}

const ChannelAmpDropdown: React.FC<ChannelAmpDropdownProps> = ({ onAddAmp, selectedAmpId, onAmpSelect, overrideName, currentAgentId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { channelGuid } = useParams<{ channelGuid?: string }>();
  const { channels, getChannel } = useChannels();
  const { amps, premadeAmps } = useAmpsLoaded();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isAgentSwitcher = !!overrideName;

  // Digital Workforce agent list — fetched once, lazily, only when this dropdown
  // is acting as an agent switcher (i.e. rendered from the chat page).
  const [dwAgents, setDwAgents] = useState<DwAgentRecord[]>([]);
  const [dwAgentsLoaded, setDwAgentsLoaded] = useState(false);
  useEffect(() => {
    if (!isAgentSwitcher || dwAgentsLoaded) return;
    listDwAgents(false, true)
      .then((records) => setDwAgents(records.filter((r) => !r.is_archived)))
      .catch(() => {})
      .finally(() => setDwAgentsLoaded(true));
  }, [isAgentSwitcher, dwAgentsLoaded]);

  // Selecting a different agent starts a fresh chat with it — mirrors
  // DigitalWorkforcePage's startChatWithAgent navigation pattern.
  const handleAgentSelect = useCallback((agent: DwAgentRecord) => {
    setOpen(false);
    if (agent.agent_id === currentAgentId) return;
    navigate(`/chat/${crypto.randomUUID()}`, { state: { dwAgentId: agent.agent_id, dwAgentName: agent.name } });
  }, [navigate, currentAgentId]);

  // Get current channel from URL
  const currentChannel = useMemo(() => {
    if (channelGuid) {
      return getChannel(channelGuid);
    }
    return null;
  }, [channelGuid, getChannel]);

  // Get AMPs for current channel
  const channelAmps = useMemo(() => {
    if (!currentChannel) return [];
    return currentChannel.ampIds
      .map(ampId => amps.find(amp => amp.id === ampId) || premadeAmps.find(amp => amp.id === ampId))
      .filter((amp): amp is Amp => amp !== undefined);
  }, [currentChannel, amps]);

  const currentLabel = useMemo(() => {
    if (currentChannel) {
      return currentChannel.name;
    }
    const ampMatch = location.pathname.match(/^\/amp\/([^/]+)/);
    if (ampMatch) {
      const matchedId = decodeURIComponent(ampMatch[1]);
      const a = amps.find((amp) => amp.id === matchedId);
      return a?.name || 'Select Workspace';
    }
    return 'Select Workspace';
  }, [currentChannel, location.pathname, amps]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, handleClickOutside]);

  // Current AMP id from URL (for highlighting active AMP)
  const currentAmpId = useMemo(() => {
    const m = location.pathname.match(/^\/amp\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.pathname]);

  // Resolve the effective amp to show in the trigger (URL amp takes priority, then selectedAmpId prop)
  const effectiveAmpId = currentAmpId || selectedAmpId || null;
  const effectiveAmp = effectiveAmpId ? amps.find(a => a.id === effectiveAmpId) : null;

  // If not in a channel context, show channels + AMPs dropdown
  if (!currentChannel) {
    const triggerLabel = overrideName || effectiveAmp?.name || currentLabel;
    return (
      <div className="channel-amp-dropdown" ref={dropdownRef}>
        <button
          className={`channel-amp-dropdown__trigger glass-tag ${isAgentSwitcher ? '' : 'channel-amp-dropdown__trigger--disabled'}`}
          onClick={isAgentSwitcher ? () => setOpen((p) => !p) : undefined /* onClick={() => setOpen((p) => !p)} */}
        >
          {overrideName ? (
            <span className="channel-amp-dropdown__override-icon">
              <RiSparklingFill size={13} />
            </span>
          ) : effectiveAmp ? (
            <span style={{ color: effectiveAmp.color }}>
              {AMP_ICON_MAP[effectiveAmp.icon] || <FiZap size={13} />}
            </span>
          ) : null}
          {triggerLabel}
          <FiChevronDown
            size={11}
            className={`channel-amp-dropdown__chevron ${open ? 'channel-amp-dropdown__chevron--open' : ''} ${isAgentSwitcher ? '' : 'channel-amp-dropdown__chevron--hidden'}`}
          />
        </button>

        <SafeAnimatePresence>
          {open && isAgentSwitcher && (
            <motion.div
              className="channel-amp-dropdown__menu glass-elevated"
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <div className="channel-amp-dropdown__section">
                <div className="channel-amp-dropdown__section-label">Digital Workforce</div>
                {dwAgents.length === 0 ? (
                  <div className="channel-amp-dropdown__empty">
                    <p>No agents available</p>
                  </div>
                ) : (
                  dwAgents.map((agent) => (
                    <button
                      key={agent.agent_id}
                      className={`channel-amp-dropdown__item ${agent.agent_id === currentAgentId ? 'channel-amp-dropdown__item--active' : ''}`}
                      onClick={() => handleAgentSelect(agent)}
                    >
                      <span className="channel-amp-dropdown__item-icon">
                        <RiSparklingFill size={13} />
                      </span>
                      {agent.name}
                      {agent.is_primary_agent && (
                        <span className="channel-amp-dropdown__primary-star" title="Primary agent">
                          <FiStar size={12} />
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          )}
          {open && !isAgentSwitcher && (
            <motion.div
              className="channel-amp-dropdown__menu glass-elevated"
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              {/* Channels section */}
              {channels.length > 0 && (
                <div className="channel-amp-dropdown__section">
                  <div className="channel-amp-dropdown__section-label">Your Workspaces</div>
                  {channels.map((ch) => (
                    <button
                      key={ch.guid}
                      className={`channel-amp-dropdown__item ${location.pathname === `/channel/${ch.guid}` ? 'channel-amp-dropdown__item--active' : ''}`}
                      onClick={() => { navigate(`/channel/${ch.guid}`); setOpen(false); }}
                    >
                      <span className="channel-amp-dropdown__item-icon" style={{ color: ch.color }}>
                        {CHANNEL_ICON_MAP[ch.icon] || <FiHash size={13} />}
                      </span>
                      {ch.name}
                      <span className="channel-amp-dropdown__amp-badge">{ch.ampIds.length} AMPs</span>
                    </button>
                  ))}
                </div>
              )}

              {/* AMPs section */}
              {amps.length > 0 && (
                <>
                  {channels.length > 0 && <div className="channel-amp-dropdown__divider" />}
                  <div className="channel-amp-dropdown__section">
                    <div className="channel-amp-dropdown__section-header">
                      <div className="channel-amp-dropdown__section-label">Your AMPs</div>
                      <span className="channel-amp-dropdown__amp-count">{amps.length}</span>
                    </div>
                    {amps.map((amp) => (
                      <button
                        key={amp.id}
                        className={`channel-amp-dropdown__item ${currentAmpId === amp.id ? 'channel-amp-dropdown__item--active' : ''}`}
                        onClick={() => { navigate(specialAmpPath(amp) ?? `/amp/${amp.id}`); setOpen(false); }}
                      >
                        <span className="channel-amp-dropdown__item-icon" style={{ color: amp.color }}>
                          {AMP_ICON_MAP[amp.icon] || <FiZap size={13} />}
                        </span>
                        {amp.name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {channels.length === 0 && amps.length === 0 && (
                <div className="channel-amp-dropdown__empty">
                  <p>No channels or AMPs yet</p>
                  <span>Create a channel or AMP to get started</span>
                </div>
              )}
            </motion.div>
          )}
        </SafeAnimatePresence>
      </div>
    );
  }

  // In channel context - show channel name and its AMPs
  return (
    <div className="channel-amp-dropdown" ref={dropdownRef}>
      <button
        className="channel-amp-dropdown__trigger glass-tag channel-amp-dropdown__trigger--disabled"
        onClick={undefined /* onClick={() => setOpen((p) => !p)} */}
        style={{ borderColor: selectedAmpId ? `${channelAmps.find(a => a.id === selectedAmpId)?.color || currentChannel.color}40` : (currentChannel.color ? `${currentChannel.color}40` : undefined) }}
      >
        <span style={{ color: selectedAmpId ? channelAmps.find(a => a.id === selectedAmpId)?.color : currentChannel.color }}>
          {selectedAmpId && channelAmps.find(a => a.id === selectedAmpId) ? (AMP_ICON_MAP[channelAmps.find(a => a.id === selectedAmpId)!.icon] || <FiZap size={13} />) : (CHANNEL_ICON_MAP[currentChannel.icon] || <FiHash size={13} />)}
        </span>
        {selectedAmpId ? channelAmps.find(a => a.id === selectedAmpId)?.name : currentLabel}
        <FiChevronDown
          size={11}
          className={`channel-amp-dropdown__chevron ${open ? 'channel-amp-dropdown__chevron--open' : ''} channel-amp-dropdown__chevron--hidden`}
        />
      </button>

      <SafeAnimatePresence>
        {open && (
          <motion.div
            className="channel-amp-dropdown__menu glass-elevated"
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {/* Current Channel Info */}
            <div className="channel-amp-dropdown__channel-info">
              <div 
                className="channel-amp-dropdown__channel-icon"
                style={{ background: `${currentChannel.color}15`, color: currentChannel.color }}
              >
                {CHANNEL_ICON_MAP[currentChannel.icon] || <FiHash size={16} />}
              </div>
              <div className="channel-amp-dropdown__channel-details">
                <div className="channel-amp-dropdown__channel-name">{currentChannel.name}</div>
                {currentChannel.description && (
                  <div className="channel-amp-dropdown__channel-desc">{currentChannel.description}</div>
                )}
              </div>
            </div>

            <div className="channel-amp-dropdown__divider" />

            {/* Channel's AMPs */}
            <div className="channel-amp-dropdown__section">
              <div className="channel-amp-dropdown__section-header">
                <div className="channel-amp-dropdown__section-label">Workspace AMPs</div>
                <span className="channel-amp-dropdown__amp-count">{channelAmps.length}</span>
              </div>
              
              {channelAmps.length === 0 ? (
                <div className="channel-amp-dropdown__empty-amps">
                  No AMPs added to this workspace
                </div>
              ) : (
                channelAmps.map((amp) => (
                  <button
                    key={amp.id}
                    className={`channel-amp-dropdown__item ${selectedAmpId === amp.id || location.pathname === `/amp/${amp.id}` ? 'channel-amp-dropdown__item--active' : ''}`}
                    onClick={() => {
                      const special = specialAmpPath(amp);
                      if (special) {
                        navigate(special);
                      } else if (onAmpSelect) {
                        onAmpSelect(amp.id);
                      } else {
                        navigate(`/amp/${amp.id}`);
                      }
                      setOpen(false);
                    }}
                  >
                    <span className="channel-amp-dropdown__item-icon" style={{ color: amp.color }}>
                      {AMP_ICON_MAP[amp.icon] || <FiZap size={13} />}
                    </span>
                    {amp.name}
                  </button>
                ))
              )}

              {/* Add AMP Button */}
              {onAddAmp && (
                <button
                  className="channel-amp-dropdown__item channel-amp-dropdown__item--add"
                  onClick={() => { onAddAmp(); setOpen(false); }}
                >
                  <span className="channel-amp-dropdown__item-icon">
                    <FiPlus size={13} />
                  </span>
                  Add AMP to Workspace
                </button>
              )}
            </div>
          </motion.div>
        )}
      </SafeAnimatePresence>
    </div>
  );
};

export default ChannelAmpDropdown;
